<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CheckMark from 'dashboard/components-next/checkbox/CheckMark.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const emit = defineEmits(['submit', 'delete', 'close']);

const inboxes = useMapGetter('inboxes/getInboxes');

const dialogRef = ref(null);
const pipeline = ref(null);
const form = ref({});

const isEdit = computed(() => Boolean(pipeline.value?.id));

const open = (existing = null) => {
  pipeline.value = existing;
  form.value = {
    name: existing?.name || '',
    description: existing?.description || '',
    inbox_ids: existing?.intake_filters?.inbox_ids || [],
    show_value: Boolean(existing?.settings?.show_value),
    auto_intake: existing ? existing.auto_intake !== false : true,
  };
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const toggleInbox = inboxId => {
  const current = [...form.value.inbox_ids];
  const index = current.indexOf(inboxId);
  if (index === -1) current.push(inboxId);
  else current.splice(index, 1);
  form.value.inbox_ids = current;
};

// Dialog emits `close` on the confirm path too. The parent uses `close` to leave the
// /kanban/new route, and after a successful create it is already navigating to the new
// board — letting both run would race, so a submit suppresses the next close.
const justSubmitted = ref(false);

const onDialogClose = () => {
  if (justSubmitted.value) {
    justSubmitted.value = false;
    return;
  }
  emit('close');
};

const onSubmit = () => {
  if (!form.value.name.trim()) return;
  justSubmitted.value = true;
  emit('submit', {
    id: pipeline.value?.id,
    payload: {
      name: form.value.name.trim(),
      description: form.value.description,
      auto_intake: form.value.auto_intake,
      // Empty array means "every inbox" on the server — the filter is only applied when
      // it has values, so there is no need for an explicit all-inboxes sentinel.
      intake_filters: { inbox_ids: form.value.inbox_ids },
      settings: { show_value: form.value.show_value, currency: 'BRL' },
    },
  });
  close();
};

const onDelete = () => {
  emit('delete', pipeline.value.id);
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEdit
        ? $t('KANBAN.PIPELINE.EDIT_TITLE')
        : $t('KANBAN.PIPELINE.NEW_TITLE')
    "
    :confirm-button-label="$t('KANBAN.PIPELINE.SAVE')"
    @confirm="onSubmit"
    @close="onDialogClose"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.PIPELINE.NAME') }}
        <input
          v-model="form.name"
          type="text"
          :placeholder="$t('KANBAN.PIPELINE.NAME_PLACEHOLDER')"
          class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ $t('KANBAN.PIPELINE.DESCRIPTION') }}
        <input
          v-model="form.description"
          type="text"
          class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        />
      </label>

      <fieldset class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak">
        <legend class="px-1 text-xs font-medium text-n-slate-11">
          {{ $t('KANBAN.PIPELINE.INTAKE') }}
        </legend>
        <p class="text-xs text-n-slate-10">
          {{ $t('KANBAN.PIPELINE.INTAKE_HINT') }}
        </p>
        <div class="flex flex-col gap-1 max-h-40 overflow-y-auto">
          <!-- A caixa é só desenho (CheckMark, D9): quem diz se está marcada é o botão. -->
          <button
            v-for="inbox in inboxes"
            :key="inbox.id"
            type="button"
            role="checkbox"
            :aria-checked="form.inbox_ids.includes(inbox.id)"
            class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded text-n-slate-12 hover:bg-n-alpha-2"
            @click="toggleInbox(inbox.id)"
          >
            <CheckMark :checked="form.inbox_ids.includes(inbox.id)" />
            <span class="truncate">{{ inbox.name }}</span>
          </button>
        </div>
      </fieldset>

      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Checkbox v-model="form.show_value" />
        {{ $t('KANBAN.PIPELINE.SHOW_VALUE') }}
      </label>

      <div
        v-if="isEdit && !pipeline.is_default"
        class="pt-2 border-t border-n-weak"
      >
        <button
          type="button"
          class="h-9 px-3 text-sm rounded-lg text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
          @click="onDelete"
        >
          {{ $t('KANBAN.PIPELINE.DELETE') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>
